import { useCallback, useEffect, useMemo } from 'react';
import { useModelBaseView, useNodeData } from '@/components/Recipe/FlowContext';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useNodeActions } from '@/components/Nodes/useNodeActions';
import { useWorkflowStore } from '@/state/workflow.state';
import { copyImage } from '@/utils/files';
import { log } from '@/logger/logger';
import { useSaveRecipe } from '@/hooks/useSaveRecipe';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { AiModelData } from '@/types/nodes/model';
import { ModelAssetToUploadedAsset } from '@/types/api/assets';
import { useMediaGalleryContext } from './MediaGalleryContext';
import { MediaGallery } from './MediaGallery';

const logger = log.getLogger('MediaGalleryWrapper');

function ModelMediaGallery() {
  const { id, closeGallery } = useMediaGalleryContext();

  const saveRecipe = useSaveRecipe();
  const { name: recipeName } = useWorkflowStore((state) => state.recipe);
  const { downloadAssetFromNode, downloadAssetsFromNodeWithProgress } = useInteractiveDownloader();

  const { setCoverImage } = useNodeActions();
  const { track } = useAnalytics();
  const nodeData = useNodeData<AiModelData>(id || '');
  const { name } = nodeData;

  const modelBaseView = useModelBaseView(id || '');

  const generations = useMemo(() => {
    return nodeData.generations?.map(ModelAssetToUploadedAsset) ?? [];
  }, [nodeData.generations]);
  const selectedGeneration = generations[nodeData.selectedIndex];
  const filteredAssets = useMemo(() => generations.filter((asset) => asset.type !== 'rendering'), [generations]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.code === 'Space') {
        e.preventDefault();
        closeGallery();
      }
    },
    [closeGallery],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleDeleteAll = useCallback(() => {
    modelBaseView.deleteAllResults();
    closeGallery();
    void saveRecipe();
  }, [modelBaseView, closeGallery, saveRecipe]);

  const handleDelete = useCallback(
    (id: string) => {
      const index = generations.findIndex((asset) => asset.type !== 'rendering' && asset.id === id);
      if (index !== -1) {
        modelBaseView.deleteResult(index);
      }
      if (generations.length === 1) {
        closeGallery();
      }
      void saveRecipe();
    },
    [generations, saveRecipe, modelBaseView, closeGallery],
  );

  const handleDeleteOthers = useCallback(
    (index: number) => {
      modelBaseView.deleteAllOtherResults(index);
      void saveRecipe();
    },
    [saveRecipe, modelBaseView],
  );

  const handleSetAsCover = () => {
    track('gallery_set_as_cover_clicked', {}, TrackTypeEnum.BI);
    if (selectedGeneration !== null && setCoverImage && selectedGeneration.type === 'image') {
      void setCoverImage(selectedGeneration.url);
    }
  };

  const handleCopyImage = async (id: string) => {
    track('media_gallery_copy_clicked', {}, TrackTypeEnum.BI);
    try {
      const file = generations.find((asset) => asset.type !== 'rendering' && asset.id === id);
      if (file?.type === 'image' && 'url' in file) {
        await copyImage(file);
      }
    } catch (err) {
      logger.error('Failed to copy image:', err as Error);
    }
  };

  const handleDownload = useCallback(
    async (filteredIndex: number) => {
      const asset = filteredAssets[filteredIndex];

      if (!asset || !id || !('url' in asset)) return;

      await downloadAssetFromNode({
        url: asset.url,
        fileType: asset.type,
        nodeName: name || id,
        prefix: recipeName,
      });
    },
    [id, name, recipeName, downloadAssetFromNode, filteredAssets],
  );

  const handleDownloadAll = useCallback(async () => {
    if (!id) return;

    const assets = modelBaseView.getAllDownloadableAssets();
    const downloadableAssets = assets.filter((asset): asset is typeof asset & { url: string } => 'url' in asset);
    await downloadAssetsFromNodeWithProgress(downloadableAssets, name || id, recipeName, undefined);
  }, [downloadAssetsFromNodeWithProgress, modelBaseView, id, name, recipeName]);

  if (!id || !selectedGeneration || selectedGeneration.type === 'rendering') {
    return null;
  }

  return (
    <MediaGallery
      title={name}
      assets={filteredAssets}
      initialSelectedId={selectedGeneration.id}
      onDelete={handleDelete}
      onDeleteOthers={handleDeleteOthers}
      onDeleteAll={handleDeleteAll}
      onSetAsCover={handleSetAsCover}
      onCopyImage={handleCopyImage}
      onDownload={handleDownload}
      onDownloadAll={handleDownloadAll}
      onClose={closeGallery}
    />
  );
}

export default ModelMediaGallery;
